<template>
    <div class="waypoint-summary">
        <div class="waypoint-summary__waypoint" v-for="(waypoint, waypointIndex) in displayedWaypoints" :key="waypointIndex" :class="{ 'waypoint-summary__waypoint--dashed' : (waypointIndex === 0 || waypointIndex === displayedWaypoints.length - 2) && togglersVisible, 'no-print' : waypoint.type === 'toggler' && !isCompact }">
            <div v-if="waypoint.displayedIndex" class="waypoint-summary__waypoint__number copy-bold ui-center-content">
                {{ waypoint.displayedIndex }}
            </div>
            <icon v-else class="waypoint-summary__waypoint__icon" :width="16" :height="16" :class="{ 'copy-light' : !showInterimAsNumbers || waypoint.type === 'toggler' }">
                <component :is="waypoint.icon"/>
            </icon>
            <div class="waypoint-summary__waypoint__label copy print-break-avoid" :class="{ 'copy-light' : !showInterimAsNumbers || waypoint.type === 'toggler' }">
                <div class="waypoint-summary__waypoint__label__title ui-center-vertically" :class="{ 'ui-cursor-pointer' : (waypoint.type === 'toggler' && compactToggleable) }" @click="toggleCompactHandler(waypoint.type)">
                    <span class="waypoint-summary__waypoint__label__title__inner" v-if="fadeCountry">
                        <span class="waypoint-summary__waypoint__label__title__address">
                            {{ waypoint.label.address }}
                            <span v-if="waypoint.type !== 'toggler'">, </span>
                        </span>
                        <span class="waypoint-summary__waypoint__label__title__country">  {{ waypoint.label.country }} </span>
                    </span>
                    <span v-else> {{ waypoint.label }} </span>
                   
                    <span v-if="waypoint.type === 'toggler' && compactToggleable" class="waypoint-summary__waypoint__toggle ui-center-vertically no-print">
                        {{ toggleLabel }}
                        <icon v-if="waypoint.togglerIcon" class="waypoint-summary__waypoint__toggle__icon" :width="isCompact ? 18 : 16" :height="isCompact ? 18 : 16">
                            <component :is="waypoint.togglerIcon" />
                        </icon>
                    </span>
                </div>
                <div class="waypoint-summary__waypoint__label__times" v-if="waypoint.times">
                    <div class="waypoint-summary__waypoint__label__time ui-center-vertically" v-for="(time, timeIndex) in waypoint.times" :key="timeIndex">
                        <span class="waypoint-summary__waypoint__label__time__item" :class="{ 'cancelled' : waypoint.isCancelled }">{{time.plannedTime}}</span>
                        <span :class="[time.realColor, { 'cancelled' : waypoint.isCancelled }]" v-if="time.realTime">{{time.realTime}}</span>
                    </div>
                </div>
                <div v-if="waypoint.distanceBetween && showDistancesBetween" class="copy-light-small">
                    {{ waypoint.distanceBetween }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from '@/components/Icon.vue'
import IconSearchMarkStart from '@/components/icons/IconSearchMarkStart.vue'
import IconSearchMarkInterim from '@/components/icons/IconSearchMarkInterim.vue'
import IconSearchMarkTarget from '@/components/icons/IconSearchMarkTarget.vue'
import IconExpand from '@/components/icons/IconExpand.vue'
import IconChevronDown from '@/components/icons/IconChevronDown.vue'
import IconChevronUp from '@/components/icons/IconChevronUp.vue'
import { TOGGLE_INTERIM_COLLAPSE_AMOUNT } from '@/data/interimInfo'

export default {
    components: {
        Icon,
        IconSearchMarkStart,
        IconSearchMarkInterim,
        IconSearchMarkTarget,
        IconExpand,
        IconChevronDown,
        IconChevronUp,
    },
    props: {
        waypoints: {
            type: Array,
            default: () => {}
        },
        compact: {
            type: Boolean,
            default: true,
        },
        onlyInterim: {
            type: Boolean,
            default: false,
        },
        compactToggleable: {
            type: Boolean,
            default: false
        },
        openLabel: {
            type: String,
            default: 'einblenden'
        },
        closeLabel: {
            type: String,
            default: 'ausblenden'
        },
        fadeCountry: {
            type: Boolean,
            default: false,
        },
        showInterimAsNumbers: {
            type: Boolean,
            default: false,
        },
        distancesBetweenWaypoints: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            isCompact: this.compact
        }
    },
    computed: {
        displayedWaypoints() {
            let result = [];
            this.waypoints.forEach((waypoint, waypointIndex) => {
                let formattedWaypoint = {
                    ...waypoint,
                    icon: this.getIcon(waypointIndex),
                }
                if(this.showInterimAsNumbers && waypointIndex !== 0 && waypointIndex !== this.waypoints.length - 1 && formattedWaypoint.type !== 'toggler') {
                    formattedWaypoint.displayedIndex = waypointIndex
                }
                if(this.fadeCountry) {
                    formattedWaypoint.label =  this.reformatWaypointLabel(formattedWaypoint.label);
                }
                if(this.distancesBetweenWaypoints?.length && this.distancesBetweenWaypoints[waypointIndex]) {
                    formattedWaypoint.distanceBetween = this.distancesBetweenWaypoints[waypointIndex]
                }
                result.push(formattedWaypoint);
            });

            if(this.waypoints.length === 2) return result

            // show only first two items and add toggler if compact is enabled
            if(this.isCompact && this.waypoints.length > TOGGLE_INTERIM_COLLAPSE_AMOUNT) {
                const toggleWaypoint = {
                    label : `${this.waypoints.length - 2} Zwischenziele`,
                    type : 'toggler',
                    icon: 'IconSearchMarkInterim',
                    togglerIcon: 'IconExpand',
                }
                result = [result[0], toggleWaypoint, result[result.length - 1]]
            }

            // add togglers on second and second to last position if compact is disabled
            if(!this.isCompact && this.waypoints.length > TOGGLE_INTERIM_COLLAPSE_AMOUNT) {
                const toggleWaypoint = {
                    label: 'Zwischenziele',
                    type: 'toggler',
                    icon: 'IconSearchMarkInterim',
                    togglerIcon: 'IconChevronDown'
                }
                result.splice(1, 0, {...toggleWaypoint, togglerIcon: 'IconChevronUp'})
                result.splice(result.length - 1, 0, {...toggleWaypoint})
                // rearrange distanceBetween to toggler element
                if(result[0].distanceBetween) {
                    result[1].distanceBetween = result[0].distanceBetween
                    result[0].distanceBetween = null
                }
            }

            // if compact version is enabled and the toggler is not visible
            if(this.compact && !this.compactToggleable) {
                let toggleWaypoint = {
                    label : `${this.waypoints.length - 2} Zwischenziele`,
                    type : 'toggler',
                    icon: 'IconSearchMarkInterim',
                }
                // special case for saved routes
                if(this.fadeCountry) {
                    toggleWaypoint = {...toggleWaypoint, ...result[1]}
                    if(this.waypoints.length > 3) toggleWaypoint.label = {
                        address: `${this.waypoints.length - 2} Zwischenziele`
                    }
                }
                result = [result[0], toggleWaypoint, result[result.length - 1]]
            }

            return result;
        },
        toggleLabel() {
            if(this.isCompact) {
                return this.openLabel
            } else {
                return this.closeLabel
            }
        },
        showDistancesBetween() {
            return (!this.isCompact || this.waypoints.length <= TOGGLE_INTERIM_COLLAPSE_AMOUNT) && this.distancesBetweenWaypoints.length > 1
        },
        togglersVisible() {
            return this.compactToggleable && this.waypoints.length > TOGGLE_INTERIM_COLLAPSE_AMOUNT
        }
    },
    methods: {
        toggleCompactHandler(type) {
            if(!this.compactToggleable) return
            if(type === 'toggler') this.isCompact = !this.isCompact
        },
        getIcon(waypointIndex) {
            let icon = 'IconSearchMarkInterim';
            if(this.onlyInterim) return icon;
            if(waypointIndex === 0) {
                icon = 'IconSearchMarkStart';
            } else if (waypointIndex === this.waypoints.length - 1) {
                icon = 'IconSearchMarkTarget';
            }
            return icon;
        },
        reformatWaypointLabel(label) {
            let result = [];
            result.address = label.split(",");
            if (result.address.length > 1) {
                result.country = result.address.pop();
            } 
            result.address = result.address.join(",")
            return result;
        },
    }
}
</script>

<style lang="scss">
.waypoint-summary {
    &__waypoint {
        display: flex;
        position: relative;

        &:not(:last-child) {
            padding-bottom: 8px;

            &::before {
                content: '';
                position: absolute;
                top: 14px;
                bottom: -2px;
                left: 7px;
                width: 2px;
                background-color: $color-grey;
            }
        }

        &--dashed {
            &:before {
                background: transparent !important;
                border-left: 2px dashed $color-grey;

                @media print {
                    background-color: $color-grey !important;
                }
            }
        }

        &__icon {
            flex-shrink: 0;
            margin-right: 8px;
        }
        &__number {
            position: relative;
            flex-shrink: 0;
            margin-right: 8px;
            width: 16px;
            height: 24px;
            line-height: 1;
            background: white;
            margin-top: -3px;
            margin-bottom: 8px;
        }
        &__label {
            margin-top: -1px;
            &__title {
                position: relative;
                &:not(:last-child) {
                    margin-bottom: 4px;
                }

                &__address,
                &__country {
                    white-space: nowrap;
                }

            }
            &__times {
                margin-bottom: 12px;
            }
            &__time {
                &__item {
                    min-width: 46px;
                    margin-right: 6px;
                }
            }
        }

        &__toggle {
            margin-left: 4px;

            &__icon {
                margin-left: 4px;
                transform: rotate(180deg);
                cursor: pointer;
            }
        }
    }
}
</style>